import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Sidebar from './components/Sidebar';
import logo from './assets/BTL_LOGO.png';

import Login from './pages/Login';
import Register from './pages/Register';
import SsoCallback from './pages/SsoCallback';

import UserDashboard from './pages/user/UserDashboard';
import MasterRequestForm from './pages/user/MasterRequestForm';
import MaterialRequestForm from './pages/user/MaterialRequestForm';
import RequestStatus from './pages/user/RequestStatus';
import DraftRequests from './pages/user/DraftRequests';

import CheckerDashboard from './pages/checker/CheckerDashboard';
import CheckerReview from './pages/checker/CheckerReview';

import ApproverDashboard from './pages/approver/ApproverDashboard';
import ApproveRequests from './pages/approver/ApproveRequests';

import ITDashboard from './pages/it_support/ITDashboard';
import IncomingRequests from './pages/it_support/IncomingRequests';

import AdminDashboard from './pages/admin/AdminDashboard';
import ManageUsers from './pages/admin/ManageUsers';
import AllRequests from './pages/admin/AllRequests';

const pageTitles = {
  '/checker/dashboard':        'Dashboard',
  '/checker/review/material':  'Material Review',
  '/checker/review/vendor':    'Vendor Review',
  '/user/dashboard': 'Dashboard',
  '/user/request/material': 'Material Master Request',
  '/user/request/vendor': 'Vendor Master Request',
  '/user/my-requests': 'My Requests',
  '/user/drafts': 'Drafts',
  '/approver/dashboard': 'Dashboard',
  '/approver/approve/material': 'Material Approvals',
  '/approver/approve/vendor': 'Vendor Approvals',
  '/it/dashboard': 'Dashboard',
  '/it/incoming/material': 'Material Requests',
  '/it/incoming/vendor': 'Vendor Requests',
  '/admin/dashboard': 'Dashboard',
  '/admin/users': 'Manage Users',
  '/admin/requests/material': 'Material Requests',
  '/admin/requests/vendor': 'Vendor Requests',
};

const TopBar = () => {
  const location = useLocation();
  const title = pageTitles[location.pathname] || 'Portal';
  const now = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  return (
    <div style={topBarStyle}>
      <div>
        <div style={{ fontSize: 19, fontWeight: 800, color: '#0B1554', letterSpacing: -0.3 }}>{title}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{now}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <img src={logo} alt="Logo" style={{ height: 38, objectFit: 'contain' }} />
      </div>
    </div>
  );
};

const topBarStyle = {
  height: 68,
  background: 'rgba(255,255,255,0.85)',
  backdropFilter: 'blur(12px)',
  borderBottom: '1px solid rgba(79,110,247,0.1)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0 32px',
  boxShadow: '0 2px 16px rgba(11,21,84,0.06)',
  position: 'sticky', top: 0, zIndex: 100,
};
const Layout = ({ children }) => (
  <div style={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(160deg,#f0f4ff 0%,#e8eeff 50%,#f5f0ff 100%)' }}>
    <Sidebar />
    <div style={{ marginLeft: 248, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <TopBar />
      <main style={{ flex: 1, padding: '28px 32px', overflowY: 'auto' }}>
        {children}
      </main>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"        element={<Login />} />
          <Route path="/register"     element={<Register />} />
          <Route path="/sso-callback" element={<SsoCallback />} />
          <Route path="/unauthorized" element={<div style={{ padding: 40, textAlign: 'center' }}><h2>403 — Access Denied</h2></div>} />

          <Route path="/user/dashboard"        element={<ProtectedRoute roles={['user']}><Layout><UserDashboard /></Layout></ProtectedRoute>} />
          <Route path="/user/request/material" element={<ProtectedRoute roles={['user']}><Layout><MaterialRequestForm /></Layout></ProtectedRoute>} />
          <Route path="/user/request/vendor"   element={<ProtectedRoute roles={['user']}><Layout><MasterRequestForm type="vendor" /></Layout></ProtectedRoute>} />
          <Route path="/user/my-requests"      element={<ProtectedRoute roles={['user']}><Layout><RequestStatus /></Layout></ProtectedRoute>} />
          <Route path="/user/drafts"           element={<ProtectedRoute roles={['user']}><Layout><DraftRequests /></Layout></ProtectedRoute>} />

          <Route path="/checker/dashboard"       element={<ProtectedRoute roles={['checker', 'admin']}><Layout><CheckerDashboard /></Layout></ProtectedRoute>} />
          <Route path="/checker/review/material" element={<ProtectedRoute roles={['checker', 'admin']}><Layout><CheckerReview source="material_requests" /></Layout></ProtectedRoute>} />
          <Route path="/checker/review/vendor"   element={<ProtectedRoute roles={['checker', 'admin']}><Layout><CheckerReview source="vendor_requests" /></Layout></ProtectedRoute>} />

          <Route path="/approver/dashboard" element={<ProtectedRoute roles={['approver', 'admin']}><Layout><ApproverDashboard /></Layout></ProtectedRoute>} />
          <Route path="/approver/approve/material" element={<ProtectedRoute roles={['approver', 'admin']}><Layout><ApproveRequests source="material_requests" /></Layout></ProtectedRoute>} />
          <Route path="/approver/approve/vendor"   element={<ProtectedRoute roles={['approver', 'admin']}><Layout><ApproveRequests source="vendor_requests" /></Layout></ProtectedRoute>} />

          <Route path="/it/dashboard" element={<ProtectedRoute roles={['it_support', 'admin']}><Layout><ITDashboard /></Layout></ProtectedRoute>} />
          <Route path="/it/incoming/material" element={<ProtectedRoute roles={['it_support', 'admin']}><Layout><IncomingRequests source="material_requests" /></Layout></ProtectedRoute>} />
          <Route path="/it/incoming/vendor"   element={<ProtectedRoute roles={['it_support', 'admin']}><Layout><IncomingRequests source="vendor_requests" /></Layout></ProtectedRoute>} />

          <Route path="/admin/dashboard" element={<ProtectedRoute roles={['admin']}><Layout><AdminDashboard /></Layout></ProtectedRoute>} />
          <Route path="/admin/users"     element={<ProtectedRoute roles={['admin']}><Layout><ManageUsers /></Layout></ProtectedRoute>} />
          <Route path="/admin/requests/material" element={<ProtectedRoute roles={['admin']}><Layout><AllRequests source="material_requests" /></Layout></ProtectedRoute>} />
          <Route path="/admin/requests/vendor"   element={<ProtectedRoute roles={['admin']}><Layout><AllRequests source="vendor_requests" /></Layout></ProtectedRoute>} />

          <Route path="/"  element={<Navigate to="/login" />} />
          <Route path="*"  element={<Navigate to="/login" />} />
        </Routes>
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false}
          toastStyle={{ borderRadius: 12, fontSize: 14, fontFamily: 'Inter, sans-serif' }} />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
